/**
 * Blocos de apresentação dos guias de procedimento do Financeiro
 * (Recebíveis Cielo › Como conciliar e Conta Corrente › Procedimentos).
 *
 * Os selos (Badge) e nomes de botão (Btn) reproduzem a aparência das telas:
 * quando um rótulo mudar lá, mude o texto nos guias também.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';

export const Section: React.FC<{ id: string; icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  id,
  icon,
  title,
  children,
}) => (
  <section id={id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3 scroll-mt-4">
    <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
      <span className="text-emerald-600">{icon}</span>
      {title}
    </h3>
    <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export const Callout: React.FC<{ tone?: 'info' | 'warn'; children: React.ReactNode }> = ({ tone = 'info', children }) => (
  <div
    className={`rounded-lg border p-3 text-sm leading-relaxed ${
      tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'
    }`}
  >
    {children}
  </div>
);

/** Selo com a mesma aparência do que aparece nas telas. */
export const Badge: React.FC<{ tone: 'green' | 'amber' | 'red' | 'sky' | 'slate'; children: React.ReactNode }> = ({
  tone,
  children,
}) => {
  const cls = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  }[tone];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
};

/** Nome de botão/link como aparece na tela. */
export const Btn: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-[12px] font-medium text-slate-800 whitespace-nowrap">
    {children}
  </span>
);

export const Steps: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ol className="space-y-2">
    {items.map((item, idx) => (
      <li key={idx} className="flex gap-3">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center">
          {idx + 1}
        </span>
        <span className="pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);

/** Um caso da conferência: como aparece, o que comparar, o que fazer e o que não fazer. */
export const Situation: React.FC<{
  badge: React.ReactNode;
  title: string;
  looks: React.ReactNode;
  compare?: React.ReactNode;
  action: React.ReactNode;
  avoid?: React.ReactNode;
}> = ({ badge, title, looks, compare, action, avoid }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200">
      {badge}
      <span className="font-medium text-slate-800">{title}</span>
    </div>
    <dl className="px-4 py-3 grid grid-cols-1 md:grid-cols-[9rem_1fr] gap-x-4 gap-y-2">
      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500 md:pt-0.5">Como aparece</dt>
      <dd>{looks}</dd>
      {compare && (
        <>
          <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500 md:pt-0.5">O que comparar</dt>
          <dd>{compare}</dd>
        </>
      )}
      <dt className="text-xs font-semibold uppercase tracking-wider text-emerald-700 md:pt-0.5">O que fazer</dt>
      <dd>{action}</dd>
      {avoid && (
        <>
          <dt className="text-xs font-semibold uppercase tracking-wider text-red-600 md:pt-0.5">Não faça</dt>
          <dd>{avoid}</dd>
        </>
      )}
    </dl>
  </div>
);

export const GoTo: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) =>
  onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-sm font-medium text-emerald-700 hover:bg-emerald-50"
    >
      {children} <ArrowRight size={14} />
    </button>
  ) : null;
